import "styles/BillUsageSection.scss";

import { useQuery } from "@tanstack/react-query";
import { UsageSection } from "components/UsageSection";
import { AggregatesQueryContext, getAggregates } from "queries/rating";
import React from "react";
import { useAppSelector } from "reduxUtils/hook";
import { AVAILABLE_PERIODS } from "utils/constants";
import { jwtClient } from "utils/jwtClient";

import { LoadingSection } from "../LoadingSection";

/**
 * @description The bill period start at date as a string or null
 */
const billPeriodStartAt = (bill: Bill): string | null =>
  bill ? bill.billing_period.period_started_at : null;

/**
 * @description The bill period end at date as a string or null
 */
const billPeriodEndAt = (bill: Bill): string | null =>
  bill ? bill.billing_period.period_ended_at : null;

/**
 * @description The start at date of the previous bill or null
 */
const previousBillStartAt = (bill: Bill): string | null =>
  bill ? bill.comparison_data.previous.started_at : null;

/**
 * @description The end at date of the previous bill or null
 */
const previousBillEndAt = (bill: Bill): string | null =>
  bill ? bill.comparison_data.previous.ended_at : null;

/**
 * @description The start date of the bill from one year ago or null
 */
const yearAgoBillStartAt = (bill: Bill): string | null =>
  bill ? bill.comparison_data.one_year_ago.started_at : null;

/**
 * @description The end date of the bill from one year ago or null
 */
const yearAgoBillEndAt = (bill: Bill): string =>
  bill ? bill.comparison_data.one_year_ago.ended_at : null;

type BillUsageSectionProps = {
  bill: Bill;
};

/**
 * @description Wrapper container for the UsageSection that gets rendered on the page of individual bills
 */
export const BillUsageSection: React.FC<BillUsageSectionProps> = ({ bill }) => {
  const { supplyNodeRef } = useAppSelector((store) => store.currentAccount);

  // Get rating aggregates for bill period
  const billPeriodQueryKey: [string, AggregatesQueryContext] = [
    "currentPeriodAggregates",
    {
      supplyNodeRef,
      startAt: new Date(billPeriodStartAt(bill)),
      endAt: new Date(billPeriodEndAt(bill)),
      jwtClient,
    },
  ];
  const {
    isFetching: isBillPeriodAggregatesFetching,
    isSuccess: isBillPeriodAggregatesSuccess,
    data: billPeriodAggregatesData,
  } = useQuery(billPeriodQueryKey, getAggregates);

  // Get rating aggregates for previous period
  const previousPeriodQueryKey: [string, AggregatesQueryContext] = [
    "previousPeriodAggregates",
    {
      supplyNodeRef,
      startAt: new Date(previousBillStartAt(bill)),
      endAt: new Date(previousBillEndAt(bill)),
      jwtClient,
    },
  ];

  const enableFetchPreviousPeriodAggregatesData =
    Boolean(previousBillStartAt(bill)) && Boolean(previousBillEndAt(bill));

  const {
    data: previousPeriodAggregatesData,
    isFetching: isPreviousPeriodAggregatesFetching,
    isSuccess: isPreviousPeriodAggregatesSuccess,
  } = useQuery(previousPeriodQueryKey, getAggregates, {
    enabled: enableFetchPreviousPeriodAggregatesData,
  });

  // Get rating aggregates for one year ago
  const yearAgoQueryKey: [string, AggregatesQueryContext] = [
    "yearAgoAggregates",
    {
      supplyNodeRef,
      startAt: new Date(yearAgoBillStartAt(bill)),
      endAt: new Date(yearAgoBillEndAt(bill)),
      jwtClient,
    },
  ];

  const enableFetchYearAgoAggregatesData =
    Boolean(yearAgoBillStartAt(bill)) && Boolean(yearAgoBillEndAt(bill));

  const {
    isFetching: isYearAgoAggregatesFetching,
    isSuccess: isYearAgoAggregatesSuccess,
    data: yearAgoAggregatesData,
  } = useQuery(yearAgoQueryKey, getAggregates, {
    enabled: enableFetchYearAgoAggregatesData,
  });

  // Error state for failed api calls is handled in the child component (UsageSection)
  if (
    isBillPeriodAggregatesFetching ||
    (enableFetchPreviousPeriodAggregatesData &&
      isPreviousPeriodAggregatesFetching) ||
    (enableFetchYearAgoAggregatesData && isYearAgoAggregatesFetching)
  ) {
    return <LoadingSection />;
  }
  const unitCode = bill.usage.unit_code;
  const { periodName } = AVAILABLE_PERIODS[bill.billing_period.duration];

  return (
    <UsageSection
      thisPeriodAggregatesData={billPeriodAggregatesData}
      previousPeriodAggregatesData={previousPeriodAggregatesData}
      yearAgoAggregatesData={yearAgoAggregatesData}
      unitCode={unitCode}
      periodName={periodName}
      completedBill={true}
      isThisPeriodAggregatesSuccess={isBillPeriodAggregatesSuccess}
      isThisPeriodAggregatesFetching={isBillPeriodAggregatesFetching}
      isPreviousPeriodAggregatesSuccess={
        enableFetchPreviousPeriodAggregatesData
          ? isPreviousPeriodAggregatesSuccess
          : false
      }
      isYearAgoAggregatesSuccess={
        enableFetchYearAgoAggregatesData ? isYearAgoAggregatesSuccess : false
      }
    />
  );
};
